import _ from 'lodash';
import {
  parseAmount,
  friendlyDate,
  friendlyTime,
  friendlyDateTime,
  superSimpleDate,
} from 'app/utils/parsers';
import IApplicationState from 'app/types/state';
import { IPaymentEvent } from './types';

interface IStatusBlock {
  paymentStatus?: string;
  transferStatus?: string;
  adminStatus?: string;
  forfeitStatus?: string;
  activeStatus?: string;
  fixtureMatchStatus?: string;
}

const defaultStatusBlock: IStatusBlock = {
  paymentStatus: 'pending',
  transferStatus: null,
  adminStatus: null,
  forfeitStatus: null,
  activeStatus: null,
  fixtureMatchStatus: null,
};

function parseMatchStatusText(status = 'active') {
  let parsedText = null;
  if (status === 'cancelled') {
    parsedText = 'Cancelled';
  }
  return parsedText;
}

function parseMatchStatusColour(
  status = 'pending',
  otherTeamStatus = 'pending',
  defaultColour = 'grey'
) {
  let parsedColour = defaultColour;
  if (status === 'cancelled') {
    parsedColour = 'black';
  }
  return parsedColour;
}

export function parsePaymentStatusText(
  status = defaultStatusBlock,
  otherTeamStatus = defaultStatusBlock,
  defaultText = 'Unpaid',
  extensivePaid = false
) {
  let parsedText = defaultText;
  if (!extensivePaid && status?.paymentStatus === 'paid') {
    parsedText = 'Teammo';
  }
  if (!extensivePaid && status?.paymentStatus === 'paid-transferred') {
    parsedText = 'Teammo';
  }
  if (extensivePaid && status?.paymentStatus === 'paid') {
    parsedText = 'Paid Teammo';
  }
  if (extensivePaid && status?.paymentStatus === 'paid-transferred') {
    parsedText = 'Paid via Transfer';
  }
  if (status?.paymentStatus === 'paid-refunded') {
    parsedText = 'Refunded';
  }
  if (
    status?.adminStatus === 'no-show'
  ) {
    parsedText = 'No-show';
  }
  if (
    otherTeamStatus?.adminStatus === 'no-show'
  ) {
    parsedText = 'Opposition No-show';
  }
  if (
    status?.adminStatus === 'cancelled' ||
    otherTeamStatus?.adminStatus === 'cancelled'
  ) {
    parsedText = 'Cancelled';
  }
  if (status?.fixtureMatchStatus === 'cancelled') {
    parsedText = 'Match Cancelled';
  }
  if (status?.forfeitStatus === 'walkover') {
    parsedText = 'Forfeit';
  }
  if (otherTeamStatus?.forfeitStatus === 'walkover') {
    parsedText = 'Opposition Forfeit';
  }
  if (status?.activeStatus === 'cancelled') {
    parsedText = 'Cancelled';
  }
  if (!extensivePaid && status?.paymentStatus === 'paid-cash') {
    parsedText = 'Cash';
  }
  if (!extensivePaid && status?.paymentStatus === 'paid-eftpos') {
    parsedText = 'Eftpos';
  }
  if (!extensivePaid && status?.paymentStatus === 'paid-prepaid') {
    parsedText = 'Pre-paid';
  }
  if (extensivePaid && status?.paymentStatus === 'paid-cash') {
    parsedText = 'Paid Cash';
  }
  if (extensivePaid && status?.paymentStatus === 'paid-eftpos') {
    parsedText = 'Paid Eftpos';
  }
  if (extensivePaid && status?.paymentStatus === 'paid-prepaid') {
    parsedText = 'Paid Pre-paid';
  }
  if (status?.transferStatus === 'transfer-required') {
    parsedText = 'Transfer Pending';
  }
  if (status?.transferStatus === 'transferred') {
    parsedText = 'Transferred';
  }
  if (status?.transferStatus === 'refunded') {
    parsedText = 'Refunded';
  }
  return parsedText;
}

function parsePaymentStatusDescription(
  status = defaultStatusBlock,
  otherTeamStatus = defaultStatusBlock,
  defaultText = null
) {
  let parsedText = defaultText;
  if (status?.transferStatus === 'transfer-required') {
    parsedText =
      'We are processing the transfer of this match payment. Once the next match is available it will be automatically carried over. If this is the last match of the season, we will provide a refund. This process takes 1-3 business days.';
  }
  if (status?.transferStatus === 'transferred') {
    parsedText = 'This match payment has been carried over to the next match.';
  }
  if (status?.transferStatus === 'refunded') {
    parsedText =
      'This match payment has been refunded to the original payment method. Please allow 1-3 business days for it to show in your account.';
  }
  return parsedText;
}

function parsePaymentStatusColour(
  status = defaultStatusBlock,
  otherTeamStatus = defaultStatusBlock,
  defaultColour = 'grey'
) {
  let parsedColour = defaultColour;
  if (status?.paymentStatus === 'paid') {
    parsedColour = 'purple';
  }
  if (status?.fixtureMatchStatus === 'cancelled') {
    parsedColour = 'black';
  }
  if (status?.paymentStatus === 'paid-cash') {
    parsedColour = 'orange';
  }
  if (status?.paymentStatus === 'paid-eftpos') {
    parsedColour = 'blue';
  }
  if (status?.paymentStatus === 'paid-prepaid') {
    parsedColour = 'yellow';
  }
  if (status?.paymentStatus === 'paid-transferred') {
    parsedColour = 'purple';
  }
  if (status?.paymentStatus === 'paid-refunded') {
    parsedColour = 'purple';
  }
  if (status?.paymentStatus === 'pending') {
    parsedColour = 'grey';
  }
  if (status?.activeStatus === 'cancelled') {
    parsedColour = 'black';
  }
  if (
    status?.adminStatus === 'cancelled' ||
    otherTeamStatus?.adminStatus === 'cancelled'
  ) {
    parsedColour = 'black';
  }
  if (
    status?.adminStatus === 'no-show' ||
    otherTeamStatus?.adminStatus === 'no-show'
  ) {
    parsedColour = 'black';
  }
  if (status?.transferStatus === 'transfer-required') {
    parsedColour = 'dark-purple';
  }
  if (status?.transferStatus === 'transferred') {
    parsedColour = 'dark-purple';
  }
  if (status?.transferStatus === 'refunded') {
    parsedColour = 'dark-purple';
  }
  if (
    status?.adminStatus === 'no-show' ||
    otherTeamStatus?.forfeitStatus === 'walkover'
  ) {
    parsedColour = 'black';
  }
  if (
    status?.forfeitStatus === 'walkover' ||
    otherTeamStatus?.forfeitStatus === 'walkover'
  ) {
    parsedColour = 'black';
  }
  return parsedColour;
}

export function parsePaymentStatus(
  status = defaultStatusBlock,
  otherTeamStatus = defaultStatusBlock,
  extensivePaid = false
) {
  // console.log('status');
  // console.log(status);
  // console.log('otherTeamStatus');
  // console.log(otherTeamStatus);
 let statuses = [];
 const colour = parsePaymentStatusColour(
   {
     paymentStatus: status?.paymentStatus,
   },
   {}
 );
 const text = parsePaymentStatusText(
   {
     paymentStatus: status?.paymentStatus,
   },
   {},
   'Unpaid',
   extensivePaid
 );
 const colour2 = parsePaymentStatusColour(
   {
     forfeitStatus: status?.forfeitStatus,
   },
   otherTeamStatus
 );
 const text2 = parsePaymentStatusText(
   {
     forfeitStatus: status?.forfeitStatus,
   },
   otherTeamStatus,
   null,
   extensivePaid
 );
 const colour3 = parsePaymentStatusColour(
   {
     adminStatus: status?.adminStatus,
     fixtureMatchStatus: status?.fixtureMatchStatus,
   },
   otherTeamStatus
 );
 const text3 = parsePaymentStatusText(
   {
     adminStatus: status?.adminStatus,
     fixtureMatchStatus: status?.fixtureMatchStatus,
   },
   otherTeamStatus,
   null,
   extensivePaid
 );
 const transferColour = parsePaymentStatusColour(
   {
     transferStatus: status?.transferStatus,
   },
   null,
   null
 );
 const transferText = parsePaymentStatusText(status, null, null);
 const transferDescription = parsePaymentStatusDescription(
   {
     transferStatus: status?.transferStatus,
   },
   null,
   null
 );

 if (
   !extensivePaid &&
   (text === 'Teammo' ||
     text === 'Cash' ||
     text === 'Eftpos' ||
     text === 'Pre-paid' ||
     text === 'Transferred' ||
     text === 'Refunded')
 ) {
   statuses.push({ colour: 'green', text: 'Paid' });
 }
 statuses.push({ colour, text });
 statuses.push({ colour: colour2, text: text2 });
 statuses.push({ colour: colour3, text: text3 });
 // console.log(text);
 // console.log(text2);
 // console.log(text2);
 // console.log(statuses);
 if (transferColour && transferText) {
   statuses.push({
     colour: transferColour,
     text: transferText,
     description: transferDescription,
   });
 }

 statuses = _.filter(statuses, (s) => !_.isEmpty(s.text));
 statuses = _.uniqBy(statuses, 'text');

 if (
   statuses.length === 2 &&
   statuses[0]?.text === 'Unpaid' &&
   (statuses[1]?.text === 'Forfeit' ||
     statuses[1]?.text === 'Opposition Forfeit' ||
     statuses[1]?.text === 'Match Cancelled' ||
     statuses[1]?.text === 'Cancelled' ||
     statuses[1]?.text === 'No-show' ||
     statuses[1]?.text === 'Opposition No-show')
 ) {
   statuses = [statuses[1]];
 }
 if (
   statuses.length === 3 &&
   statuses[0]?.text === 'Unpaid' &&
   (statuses[1]?.text === 'Forfeit' &&
     statuses[2]?.text === 'No-show')
 ) {
   statuses = [statuses[1], statuses[2]];
 }
 statuses = _.uniqBy(statuses, 'text');
 return statuses;
}

interface IPaymentCardData {
  paymentEventId: string;
  id: string;
  matchPaymentId: string;
  forfeitId: string;
  round: string;
  teamName: string;
  dateTime: string;
  syncedAt: string;
  eventInfo: string;
  groupId: string;
  teamId: string;
  amount: string;
  matchStatus: string;
  statusBlock: IStatusBlock;
  otherTeamStatusBlock: IStatusBlock;
  paymentEnabled: boolean;
  teamPaymentRequest?: any;
  section: string;
  time: string;
  timeRaw: string;
  location: string;
  statuses: [
    {
      colour: string;
      text: string;
    }
  ];
  status: string;
  otherTeamStatus: string;
  metadata: any;
  paymentCategory: string;
  dragSelected?: boolean;
  selected?: boolean;
}

function getPaymentEventForGroup(
  paymentEvent,
  groupId
): {
  name: string;
  amount: string;
  statuses: [
    {
      colour: string;
      text: string;
    }
  ];
  status: string;
  statusBlock: IStatusBlock;
  otherTeamStatusBlock: IStatusBlock;
  otherTeamStatus: string;
  matchPaymentId: string;
  forfeitId: string;
  paymentEventId: string;
  metadata: any;
  paymentCategory: string;
  noOfRegularPlayers: number;
  noOfFillInPlayers: number;
} {
  let groupData = {};
  if (_.isEmpty(paymentEvent.groupOne) || _.isEmpty(paymentEvent.groupTwo)) {
    return {};
  }

  const groupOneStatusBlock = {
    paymentStatus: paymentEvent.groupOneMatchPaymentStatus,
    transferStatus: paymentEvent.groupOneMatchPaymentTransferStatus,
    adminStatus: paymentEvent.groupOneMatchPaymentAdminStatus,
    forfeitStatus: paymentEvent.groupOneMatchPaymentForfeitStatus,
    activeStatus: paymentEvent.groupOneMatchPaymentActiveStatus,
    fixtureMatchStatus: paymentEvent.status,
  };

  const groupTwoStatusBlock = {
    paymentStatus: paymentEvent.groupTwoMatchPaymentStatus,
    transferStatus: paymentEvent.groupTwoMatchPaymentTransferStatus,
    adminStatus: paymentEvent.groupTwoMatchPaymentAdminStatus,
    forfeitStatus: paymentEvent.groupTwoMatchPaymentForfeitStatus,
    activeStatus: paymentEvent.groupTwoMatchPaymentActiveStatus,
    fixtureMatchStatus: paymentEvent.status,
  };

  if (paymentEvent.groupOne.id === groupId) {
    // const matchPaymentId =
    //   paymentEvent.groupOneMatchPaymentForfeitStatus === 'walkover'
    //     ? paymentEvent.groupOneForfeit.id
    //     : paymentEvent.groupOneMatchPayment.id;

    groupData = {
      name: paymentEvent.groupOneName,
      amount: parseAmount(paymentEvent.groupOneMatchPaymentAmount),
      statuses: parsePaymentStatus(groupOneStatusBlock, groupTwoStatusBlock, true),
      status: paymentEvent.groupOneMatchPaymentStatus,
      statusBlock: groupOneStatusBlock,
      otherTeamStatusBlock: groupTwoStatusBlock,
      otherTeamStatus: paymentEvent.groupTwoMatchPaymentStatus,
      matchPaymentId: paymentEvent.groupOneMatchPayment.id,
      forfeitId: paymentEvent.groupOneForfeit.id,
      paymentEventId: paymentEvent.id,
      metadata: paymentEvent.groupOnePaymentMetadata,
      paymentCategory: paymentEvent.groupOneMatchPaymentType,
      noOfRegularPlayers: paymentEvent.groupOneMatchPaymentNoOfRegularPlayers,
      noOfFillInPlayers: paymentEvent.groupOneMatchPaymentNoOfFillInPlayers,
    };
  }
  if (paymentEvent.groupTwo.id === groupId) {
    // const matchPaymentId =
    //   paymentEvent.groupTwoMatchPaymentForfeitStatus === 'walkover'
    //     ? paymentEvent.groupTwoForfeit.id
    //     : paymentEvent.groupTwoMatchPayment.id;

    groupData = {
      name: paymentEvent.groupTwoName,
      amount: parseAmount(paymentEvent.groupTwoMatchPaymentAmount),
      statuses: parsePaymentStatus(groupTwoStatusBlock, groupOneStatusBlock, true),
      status: paymentEvent.groupTwoMatchPaymentStatus,
      statusBlock: groupTwoStatusBlock,
      otherTeamStatusBlock: groupOneStatusBlock,
      otherTeamStatus: paymentEvent.groupOneMatchPaymentStatus,
      matchPaymentId: paymentEvent.groupTwoMatchPayment.id,
      forfeitId: paymentEvent.groupTwoForfeit.id,
      paymentEventId: paymentEvent.id,
      metadata: paymentEvent.groupTwoPaymentMetadata,
      paymentCategory: paymentEvent.groupTwoMatchPaymentType,
      noOfRegularPlayers: paymentEvent.groupTwoMatchPaymentNoOfRegularPlayers,
      noOfFillInPlayers: paymentEvent.groupTwoMatchPaymentNoOfFillInPlayers,
    };
  }
  return groupData;
}

export function getCurrentPaymentEvent(state) {
  if (
    state.paymentEvents.paymentEventsById !== undefined &&
    state.paymentEvents.currentPaymentEventId !== undefined
  ) {
    const findPaymentEvent =
      state.paymentEvents.paymentEventsById[
        state.paymentEvents.currentPaymentEventId
      ];
    const currentPaymentEvent = {
      ...findPaymentEvent,
    };

    return currentPaymentEvent;
  }
  return {};
}

export function getPaymentEventsByIdSortedDate(
  paymentEventsById,
  currentDateTime = new Date()
): any {
  if (paymentEventsById !== undefined) {
    const sortedPaymentEventsById = {};

    const pastArray = [];
    const futureArray = [];
    // Show matches until end of day
    currentDateTime.setHours(0, 0, 0, 0);

    _.map(paymentEventsById, (paymentEvent) => {
      // console.log(friendlyDateTime(paymentEvent.eventAt));
      // console.log(friendlyDateTime(currentDateTime));
      if (new Date(paymentEvent.eventAt) < currentDateTime) {
        pastArray.push(paymentEvent);
      } else {
        futureArray.push(paymentEvent);
      }
    });

    const sortedPastArray = pastArray.sort(
      (a, b) => new Date(b.eventAt) - new Date(a.eventAt)
    );
    const sortedFutureArray = futureArray.sort(
      (a, b) => new Date(a.eventAt) - new Date(b.eventAt)
    );
    const sortedArray = sortedFutureArray.concat(sortedPastArray);

    _.each(sortedArray, (pe) => {
      sortedPaymentEventsById[pe.id] = pe;
    });
    return sortedPaymentEventsById;
  }
  return {};
}

export function filterPaymentEventByStatus(
  event: any,
  groupId: string | string[],
  status: string
): boolean {
  const groupData = getPaymentEventForGroup(event, groupId);
  switch (status) {
    case 'pending':
      return (
        _.includes(['pending'], groupData.statusBlock.paymentStatus) &&
        !_.includes(['walkover'], groupData.otherTeamStatusBlock.forfeitStatus)
      );
    case 'paid':
      return (
        _.includes(
          [
            'paid',
            'paid-cash',
            'paid-eftpos',
            'paid-transferred',
            'paid-refunded',
            'paid-prepaid',
          ],
          groupData.statusBlock.paymentStatus
        ) &&
        !_.includes(['walkover'], groupData.otherTeamStatusBlock.forfeitStatus)
      );
    case 'refunded':
      return (
        _.includes(['refunded'], groupData.statusBlock.forfeitStatus) &&
        !_.includes(['walkover'], groupData.otherTeamStatusBlock.forfeitStatus)
      );
    case 'forfeited':
      return (
        _.includes(['walkover'], groupData.statusBlock.forfeitStatus) ||
        _.includes(['walkover'], groupData.otherTeamStatusBlock.forfeitStatus)
      );
    case 'all':
      return true;
    default:
      return true;
  }
}

export function filterPaymentEventByStatusSimple(
  event: any,
  groupId: string | string[],
  status: string = 'all',
  currentDateTime = new Date()
): boolean {
  const groupData = getPaymentEventForGroup(event, groupId);
  switch (status) {
    case 'upcoming':
      return new Date(event.eventAt) >= currentDateTime;
    case 'paid':
      return (
        event.status == 'cancelled' ||
        _.includes(
          [
            'paid',
            'paid-cash',
            'paid-eftpos',
            'paid-transferred',
            'paid-refunded',
            'paid-prepaid',
          ],
          groupData.statusBlock.paymentStatus
        ) ||
        _.includes(
          ['refunded', 'walkover'],
          groupData.statusBlock.forfeitStatus
        ) ||
        _.includes(['no-show', 'cancelled'], groupData.otherTeamStatusBlock.adminStatus) ||
        _.includes(['walkover'], groupData.otherTeamStatusBlock.forfeitStatus)
      );
    case 'overdue':
      return (
        event.status !== 'cancelled' &&
        _.includes(['pending'], groupData.statusBlock.paymentStatus) &&
        new Date(event.eventAt) < currentDateTime
      );
    case 'all':
      return true;
    default:
      return true;
  }
}

export function getPaymentEventsByIdSortedDateFilterByStatus(
  paymentEventsById: any,
  groupId: string | string[],
  status = 'all'
): any {
  if (paymentEventsById !== undefined) {
    const filteredPaymentEventsById = _.filter(paymentEventsById, (event) => {
      return filterPaymentEventByStatus(event, groupId, status);
    });

    return getPaymentEventsByIdSortedDate(filteredPaymentEventsById);
  }
  return {};
}

export function getPaymentEventsByIdSortedStatus(
  paymentEventGroupMap: {
    [key: string]: {
      paymentEventId: string;
      groupId: string;
    };
  },
  paymentEventsById: any,
  groupId: string | string[],
  currentDateTime = new Date()
): {
  upcoming: any;
  overdue: any;
  paid: any;
} {
  if (paymentEventsById !== undefined) {
    const sortedPaidPaymentEventsById = {};
    const sortedUpcomingPaymentEventsById = {};
    const sortedOverduePaymentEventsById = {};

    const upcomingArray = [];
    const paidArray = [];
    const overdueArray = [];
    // Show matches until end of day
    currentDateTime.setHours(0, 0, 0, 0);

    _.map(paymentEventGroupMap, (paymentEventGroupObject) => {
      const paymentEvent =
        paymentEventsById[paymentEventGroupObject.paymentEventId];

      if (
        filterPaymentEventByStatusSimple(
          paymentEvent,
          paymentEventGroupObject.groupId,
          'paid',
          currentDateTime
        )
      ) {
        paidArray.push({
          paymentEvent: paymentEvent,
          groupId: paymentEventGroupObject.groupId,
        });
      } else if (
        filterPaymentEventByStatusSimple(
          paymentEvent,
          paymentEventGroupObject.groupId,
          'upcoming',
          currentDateTime
        )
      ) {
        upcomingArray.push({
          paymentEvent: paymentEvent,
          groupId: paymentEventGroupObject.groupId,
        });
      } else if (
        filterPaymentEventByStatusSimple(
          paymentEvent,
          paymentEventGroupObject.groupId,
          'overdue',
          currentDateTime
        )
      ) {
        overdueArray.push({
          paymentEvent: paymentEvent,
          groupId: paymentEventGroupObject.groupId,
        });
      }
    });

    const sortedPaidArray = paidArray.sort(
      (a, b) =>
        new Date(b.paymentEvent.eventAt) - new Date(a.paymentEvent.eventAt)
    );
    const sortedUpcomingArray = upcomingArray.sort(
      (a, b) =>
        new Date(a.paymentEvent.eventAt) - new Date(b.paymentEvent.eventAt)
    );
    const sortedOverdueArray = overdueArray.sort(
      (a, b) =>
        new Date(a.paymentEvent.eventAt) - new Date(b.paymentEvent.eventAt)
    );

    _.each(sortedPaidArray, (pe) => {
      sortedPaidPaymentEventsById[pe.paymentEvent.id] = pe;
    });
    _.each(sortedUpcomingArray, (pe) => {
      sortedUpcomingPaymentEventsById[pe.paymentEvent.id] = pe;
    });
    _.each(sortedOverdueArray, (pe) => {
      sortedOverduePaymentEventsById[pe.paymentEvent.id] = pe;
    });
    return {
      paid: sortedPaidArray,
      upcoming: sortedUpcomingArray,
      overdue: sortedOverdueArray,
    };
  }
  return {
    paid: [],
    upcoming: [],
    overdue: [],
  };
}

export function getPaymentEvents(state) {
  return {
    ...state.paymentEvents,
    currentPaymentEvent: {
      ...getCurrentPaymentEvent(state),
    },
    paymentEventsByIdSortedDate: getPaymentEventsByIdSortedDate(
      state.paymentEvents.paymentEventsById
    ),
  };
}

export function getPaymentEventsByIds(paymentEventsById, paymentEventIds) {
  if (paymentEventsById !== undefined && paymentEventIds != null) {
    return _.pick(paymentEventsById, paymentEventIds);
  }
  return {};
}

export function getPaymentEventsByObjectId(
  paymentEventsById,
  object,
  objectId
) {
  if (paymentEventsById !== undefined && objectId != null) {
    // console.log(paymentEvents, objectId);
    const filteredPaymentEvents = _.filter(paymentEventsById, {
      [object]: { id: objectId },
    });

    const paymentEventIds = _.map(filteredPaymentEvents, 'id');
    const newPaymentEventsById = _.pick(paymentEventsById, paymentEventIds);

    return {
      paymentEventsById: newPaymentEventsById,
      paymentEventIds,
    };
  }
  return {};
}

export function getPaymentEventForMatchCard(
         paymentEvent,
         groupId,
         teamId = undefined,
         teamPaymentRequest = undefined,
         dragSelected = false,
         rows = {}
       ): IPaymentCardData {
         if (_.isEmpty(paymentEvent)) {
           return undefined;
         }
         const groupData = getPaymentEventForGroup(paymentEvent, groupId);
         if (_.isEmpty(groupData)) {
           return undefined;
         }
         return {
           paymentEventId: groupData.paymentEventId,
           id: groupData.matchPaymentId,
           matchPaymentId: groupData.matchPaymentId,
           forfeitId: groupData.forfeitId,
           teamName: groupData.name,
           round: paymentEvent.description,
           dateTime: superSimpleDate(paymentEvent.eventAt),
           syncedAt: friendlyDate(paymentEvent.syncedAt),
           eventInfo: paymentEvent.eventInfo,
           amount: groupData.amount,
           groupId,
           section: paymentEvent.sectionTag,
           time: friendlyTime(paymentEvent.eventAt),
           timeRaw: paymentEvent.eventAt,
           location: paymentEvent.locationInfo,
           noOfRegularPlayers: groupData.noOfRegularPlayers,
           noOfFillInPlayers: groupData.noOfFillInPlayers,
           noOfTotalPlayers: (groupData.noOfRegularPlayers || 0) + (groupData.noOfFillInPlayers || 0),
           statuses: groupData.statuses,
           status: groupData.status,
           statusBlock: groupData.statusBlock,
           otherTeamStatusBlock: groupData.otherTeamStatusBlock,
           matchStatus: paymentEvent.status,
           otherTeamStatus: groupData.otherTeamStatus,
           paymentCategory: groupData.paymentCategory,
           metadata: groupData.metadata,
           paymentEnabled: paymentEvent.paymentEnabled,
           teamId,
           teamPaymentRequest,
           dragSelected,
           selected: rows[groupData.matchPaymentId]?.selected,
         };
       }
export function getGroupName(paymentEvent, groupId): string {
  const groupData = getPaymentEventForGroup(paymentEvent, groupId);

  if (_.isEmpty(groupData)) {
    return undefined;
  }
  return groupData.name;
}
